/**
 * Shown to a signed-in user whose account has `status='pending_approval'`.
 *
 * The user successfully authenticated with Google — we have their
 * email and display name — but the super admin hasn't yet approved
 * them. They can't do anything else until approval, so the page just
 * acknowledges the wait and offers a sign-out button (useful if they
 * signed in with the wrong Google account and want to try another).
 */

import { useAuth } from "@/features/auth/useAuth";
import { useLogout } from "@/features/auth/useLogout";

export function PendingApprovalPage() {
  const { user } = useAuth();
  const logout = useLogout();

  return (
    <main className="max-w-[640px] mx-auto px-6 md:px-11 pt-24 pb-16 flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          Account pending
        </div>
        <h1 className="font-serif text-display-md text-ink">
          <em className="italic font-normal text-coral-deep">You're in line.</em>
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          Your account needs super-admin approval before you can use Make It
          Count. The super admin reviews each request before activating it —
          usually within a day.
        </p>
      </div>

      {user && (
        <div className="rounded-md border border-edge bg-surface px-5 py-4 max-w-[560px]">
          <div className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted mb-1">
            Signed in as
          </div>
          <div className="font-sans text-[15px] font-medium text-ink">
            {user.display_name}
          </div>
          <div className="font-mono text-[13px] text-ink-soft mt-[2px]">{user.email}</div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <p className="font-sans text-[13px] text-ink-muted italic max-w-[560px]">
          Wrong account? Sign out and try a different one.
        </p>
        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="self-start px-5 py-2 rounded-md font-sans text-[14px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        >
          {logout.isPending ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </main>
  );
}
